import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { DocMeta, Kind } from "../../shared/types";
import { KINDS, KIND_LABEL } from "../../shared/types";
import { api } from "../api";
import { useSession } from "../session";
import DocCard from "../components/DocCard";
import { FunnelIcon, PlusIcon, SearchIcon } from "../components/icons";

export default function IndexPage() {
  const navigate = useNavigate();
  const { user, signOut } = useSession();

  const [documents, setDocuments] = useState<DocMeta[]>([]);
  const [kind, setKind] = useState<Kind | null>(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const trimmed = query.trim();
    const filters = kind ? { kind } : {};

    let live = true;
    // Debounced so a keystroke does not become a query.
    const timer = setTimeout(() => {
      setLoading(true);
      const request = trimmed ? api.search(trimmed, filters) : api.list(filters);
      request
        .then((result) => live && setDocuments(result.documents))
        .catch((err: Error) => live && setError(err.message))
        .finally(() => live && setLoading(false));
    }, trimmed ? 180 : 0);

    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [query, kind]);

  const createDocument = useCallback(async () => {
    setCreating(true);
    try {
      const { document } = await api.create({ title: "Untitled document" });
      navigate(`/d/${document.slug}/edit`);
    } catch (err) {
      setError((err as Error).message);
      setCreating(false);
    }
  }, [navigate]);

  return (
    <div className="shell">
      <header className="topbar">
        <div className="masthead">
          <h1>Handbook</h1>
          <p>
            Playbooks say how we do it. Policies say what we will and will not do. Decision
            records say why, so the argument is not had twice.
          </p>
        </div>

        <div className="topbar-actions">
          <label className="searchbox">
            <SearchIcon />
            <input
              type="search"
              value={query}
              placeholder="Search documents"
              aria-label="Search documents"
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <button className="btn btn-primary" onClick={createDocument} disabled={creating}>
            <PlusIcon />
            New document
          </button>
        </div>
      </header>

      <div className="filters">
        <span className="funnel">
          <FunnelIcon />
        </span>
        <button className="chip" aria-pressed={kind === null} onClick={() => setKind(null)}>
          All
        </button>
        {KINDS.map((option) => (
          <button
            key={option}
            className="chip"
            aria-pressed={kind === option}
            onClick={() => setKind(option)}
          >
            {KIND_LABEL[option]}
          </button>
        ))}

        <span style={{ marginLeft: "auto" }} className="who">
          {user?.picture && <img src={user.picture} alt="" />}
          {user?.name}
          <button className="btn btn-quiet" onClick={signOut}>
            Sign out
          </button>
        </span>
      </div>

      {error && <div className="notice">{error}</div>}

      {!error && loading && documents.length === 0 && (
        <div className="notice">Loading…</div>
      )}

      {!error && !loading && documents.length === 0 && (
        <div className="notice">
          {query.trim()
            ? `Nothing matches “${query.trim()}”.`
            : "No documents yet. Start with a playbook."}
        </div>
      )}

      <div className="cards">
        {documents.map((doc) => (
          <DocCard key={doc.id} doc={doc} />
        ))}
      </div>
    </div>
  );
}
